'use client';

import type { ProductUnitItem } from '@weberus/shared-types';
import type { VerticalProductTabItemProps } from '@weberus/ui/components';
import type { Swiper as SwiperCore } from 'swiper';
import type { FC } from 'react';
import { useMemo, useRef } from 'react';
import { chunk } from 'lodash-es';
import { useAtomValue } from 'jotai';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SubHeader } from '@yanolja-rnd/nol-design-system';
import { VerticalProductTabItem } from '@weberus/ui/components';
import { YaTrackerImpression } from '@weberus/client-tracker';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { SwiperNavButton } from '@/app/_components/common';
import { cn } from '@/app/_shared/utils/tw-cn';
import { convertProductUnitToVerticalProps } from '@/app/(pages)/results/utils/convert-product-unit-to-vertical-props';

interface RecommendWidgetProps {
  header: { title: string; keyword?: string; subTitle?: string };
  items: ProductUnitItem[];
  position: number;
  isNoResult?: boolean;
  serverLogMeta?: Record<string, unknown>;
}

const RecommendSwipe = ({
  className,
  items,
  onClickItem,
}: {
  className?: string;
  items: VerticalProductTabItemProps[];
  onClickItem?: (item: VerticalProductTabItemProps) => void;
}) => {
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const swiperRef = useRef<SwiperCore | null>(null);

  // PC일 경우 4개씩, 모바일일 경우 1개씩 슬라이드
  const allSlides: { items: VerticalProductTabItemProps[]; chunkIndex: number }[] = useMemo(() => {
    const itemsPerSlide = isPcWidth ? 4 : 1;
    const chunks = chunk(items, itemsPerSlide); // lodash chunk 사용

    const slides = chunks.map((itemChunk, chunkIndex) => ({
      items: itemChunk,
      chunkIndex,
    }));

    return slides;
  }, [items, isPcWidth]);

  const showNavButton = items.length > 4 && isPcWidth;

  const handleItemImpression = (item: VerticalProductTabItemProps) => {
    console.log(item);
  };

  const handleClickItem = async (e: React.MouseEvent<HTMLAnchorElement>, item: VerticalProductTabItemProps) => {
    e.preventDefault();
    onClickItem?.(item);
  };

  return (
    <div className={cn('relative', className)}>
      <Swiper
        loop={isPcWidth && allSlides.length > 1}
        slidesPerView="auto"
        spaceBetween={isPcWidth ? 0 : 8}
        onSwiper={(swiper: SwiperCore) => {
          swiperRef.current = swiper;
        }}>
        {isPcWidth ? (
          <>
            {allSlides.map((slide, slideIndex) => (
              <SwiperSlide key={`pc_${slideIndex}`}>
                <div className="flex w-full flex-row gap-12">
                  {slide.items.map((item: VerticalProductTabItemProps, itemIndex) => (
                    <YaTrackerImpression
                      key={`item_${slideIndex}_${itemIndex}`}
                      onImpressed={() => handleItemImpression(item)}>
                      <VerticalProductTabItem
                        {...item}
                        titleClassName="typography-subtitle-18-regular-line-clamp-2"
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleClickItem(e, item)}
                      />
                    </YaTrackerImpression>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </>
        ) : (
          <>
            {allSlides.map((slide, slideIndex) => (
              <SwiperSlide key={`mobile_${slideIndex}`} className="!w-fit">
                {slide.items.map((item: VerticalProductTabItemProps, itemIndex) => (
                  <YaTrackerImpression
                    key={`item_${slideIndex}_${itemIndex}`}
                    onImpressed={() => handleItemImpression(item)}>
                    <div key={itemIndex} className="w-[104px]">
                      <VerticalProductTabItem {...item} mobileRatio="3_4" onClick={(e) => handleClickItem(e, item)} />
                    </div>
                  </YaTrackerImpression>
                ))}
              </SwiperSlide>
            ))}
          </>
        )}
      </Swiper>

      {!!showNavButton && <SwiperNavButton swiperRef={swiperRef} top={100} />}
    </div>
  );
};

export const RecommendWidget: FC<RecommendWidgetProps> = ({ header, items, isNoResult, serverLogMeta }) => {
  const convertedItems: VerticalProductTabItemProps[] = useMemo(() => {
    return convertProductUnitToVerticalProps(items);
  }, [items]);

  if (isNoResult) {
    return null;
  }

  const handleClickItem = (item: VerticalProductTabItemProps) => {
    //TODO : clientLog
    console.log('Item clicked:', item);
    console.log('serverLogMeta:', serverLogMeta);
  };

  return (
    <>
      <SubHeader
        className="px-16"
        theme="ghost"
        title={
          <>
            <span className="text-text-neutral-main typography-subtitle-18-bold">&apos;</span>
            <span className="text-text-primary-main typography-subtitle-18-bold">{header?.keyword || ''}</span>
            <span className="text-text-neutral-main typography-subtitle-18-bold">
              &apos; 검색한 고객들이 이런 상품도 봤어요{' '}
            </span>
          </>
        }
      />
      <RecommendSwipe className="px-16 pc:px-0" items={convertedItems} onClickItem={(item) => handleClickItem(item)} />
    </>
  );
};
